import { ref } from 'vue'
import { subscribePush, unsubscribePush } from '@/services/api'

export function usePushSubscription() {
  const supported = 'serviceWorker' in navigator && 'PushManager' in window
  const subscribed = ref(false)
  const loading = ref(false)
  const error = ref('')

  async function getSwReg() {
    if (!supported) return null
    try {
      return await navigator.serviceWorker.ready
    } catch {
      return null
    }
  }

  async function checkSubscription() {
    if (!supported) return false
    const reg = await getSwReg()
    if (!reg) return false
    const sub = await reg.pushManager.getSubscription()
    subscribed.value = !!sub
    return !!sub
  }

  async function subscribeToPush() {
    error.value = ''
    if (!supported) {
      error.value = 'Push not supported on this browser'
      return
    }
    if (Notification.permission === 'denied') {
      error.value = 'Notification permission denied'
      return
    }

    if (Notification.permission !== 'granted') {
      const result = await Notification.requestPermission()
      if (result !== 'granted') {
        error.value = 'Notification permission not granted'
        return
      }
    }

    loading.value = true
    try {
      const reg = await getSwReg()
      if (!reg) {
        error.value = 'Service worker not ready'
        return
      }

      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
      if (!vapidKey) {
        error.value = 'VAPID public key not configured'
        return
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      await subscribePush({
        endpoint: sub.endpoint,
        keys: sub.toJSON().keys,
      })

      subscribed.value = true
    } catch (e) {
      console.warn('[Push] Subscribe failed:', e)
      error.value = e.message || 'Subscribe failed'
    } finally {
      loading.value = false
    }
  }

  async function unsubscribeFromPush() {
    error.value = ''
    loading.value = true
    try {
      const reg = await getSwReg()
      if (!reg) return
      const sub = await reg.pushManager.getSubscription()
      if (!sub) return

      await unsubscribePush({ endpoint: sub.endpoint })
      await sub.unsubscribe()
      subscribed.value = false
    } catch (e) {
      console.warn('[Push] Unsubscribe failed:', e)
      error.value = e.message || 'Unsubscribe failed'
    } finally {
      loading.value = false
    }
  }

  return {
    supported,
    subscribed,
    loading,
    error,
    checkSubscription,
    subscribeToPush,
    unsubscribeFromPush,
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)))
}
